import { Plus, X, Save, Copy } from 'lucide-react'
import type { StakeholderTemplate } from '@bandos/shared'
import { DismissibleTooltip } from '../../../../common/DismissibleTooltip'
import type { FormData, ArrayField } from '../CharacterSelectionModal.types'
import {
  CreateFormContainer,
  CreateFormHeader,
  CreateFormContent,
  CreateFormField,
  CreateFormLabel,
  CreateFormInput,
  CreateFormTextarea,
  CreateFormArrayField,
  CreateFormArrayItem,
  CreateFormArrayInput,
  CreateFormArrayRemove,
  CreateFormArrayAdd,
  CreateFormFooter,
  ProfileActionButton,
} from '../styles/CharacterSelectionModalStyles'

interface StakeholderFormProps {
  mode: 'create' | 'edit'
  formData: FormData
  editingTemplate: StakeholderTemplate | null
  submitting: boolean
  updateFormField: (field: keyof FormData, value: string | string[]) => void
  addArrayItem: (field: ArrayField) => void
  removeArrayItem: (field: ArrayField, index: number) => void
  updateArrayItem: (field: ArrayField, index: number, value: string) => void
  onCancel: () => void
  onCreateSubmit: () => void
  onEditSave: () => void
  onSaveAsNew: () => void
}

interface ArrayFieldConfig {
  field: ArrayField
  label: string
  placeholder: string
  addLabel: string
}

const ARRAY_FIELDS: ArrayFieldConfig[] = [
  {
    field: 'goals',
    label: 'Goals',
    placeholder: 'e.g., Increase user retention by 20%',
    addLabel: 'Add Goal',
  },
  {
    field: 'concerns',
    label: 'Concerns',
    placeholder: 'e.g., Feature creep delaying the launch',
    addLabel: 'Add Concern',
  },
  {
    field: 'success_metrics',
    label: 'Success Metrics',
    placeholder: 'e.g., Monthly active users, NPS score',
    addLabel: 'Add Metric',
  },
  {
    field: 'extra_rules',
    label: 'Feedback Rules',
    placeholder: 'e.g., Always asks about timeline impact',
    addLabel: 'Add Rule',
  },
]

export function StakeholderForm({
  mode,
  formData,
  editingTemplate,
  submitting,
  updateFormField,
  addArrayItem,
  removeArrayItem,
  updateArrayItem,
  onCancel,
  onCreateSubmit,
  onEditSave,
  onSaveAsNew,
}: StakeholderFormProps) {
  const isPublic = editingTemplate?.is_public ?? false
  const isDisabled = submitting || !formData.name.trim()

  const renderArrayField = ({ field, label, placeholder, addLabel }: ArrayFieldConfig) => (
    <CreateFormField key={field}>
      <CreateFormLabel>{label}</CreateFormLabel>
      <CreateFormArrayField>
        {formData[field].map((value, index) => (
          <CreateFormArrayItem key={index}>
            <CreateFormArrayInput
              value={value}
              onChange={(e) => updateArrayItem(field, index, e.target.value)}
              placeholder={placeholder}
            />
            {formData[field].length > 1 && (
              <CreateFormArrayRemove onClick={() => removeArrayItem(field, index)}>
                <X size={14} />
              </CreateFormArrayRemove>
            )}
          </CreateFormArrayItem>
        ))}
        <CreateFormArrayAdd onClick={() => addArrayItem(field)}>
          <Plus size={12} />
          {addLabel}
        </CreateFormArrayAdd>
      </CreateFormArrayField>
    </CreateFormField>
  )

  return (
    <CreateFormContainer>
      <CreateFormHeader>
        {mode === 'create'
          ? 'Create New Team Member'
          : isPublic
            ? 'Customize Template'
            : 'Edit Team Member'}
      </CreateFormHeader>

      <CreateFormContent>
        <CreateFormField>
          <CreateFormLabel>Name *</CreateFormLabel>
          <CreateFormInput
            value={formData.name}
            onChange={(e) => updateFormField('name', e.target.value)}
            placeholder="Sarah Chen"
          />
        </CreateFormField>

        <CreateFormField>
          <CreateFormLabel>Role</CreateFormLabel>
          <CreateFormInput
            value={formData.role}
            onChange={(e) => updateFormField('role', e.target.value)}
            placeholder="Product Manager"
          />
        </CreateFormField>

        <CreateFormField>
          <CreateFormLabel>Role Summary</CreateFormLabel>
          <CreateFormTextarea
            value={formData.role_summary}
            onChange={(e) => updateFormField('role_summary', e.target.value)}
            placeholder="Brief description of what this person does and cares about"
          />
        </CreateFormField>

        {ARRAY_FIELDS.map(renderArrayField)}
      </CreateFormContent>

      <CreateFormFooter>
        <ProfileActionButton onClick={onCancel}>Cancel</ProfileActionButton>
        {mode === 'create' ? (
          <ProfileActionButton $variant="primary" onClick={onCreateSubmit} disabled={isDisabled}>
            {submitting ? 'Creating...' : 'Create'}
          </ProfileActionButton>
        ) : (
          <>
            {isPublic ? (
              <DismissibleTooltip title="Public templates cannot be modified. Save as a new custom member instead.">
                <span style={{ display: 'inline-flex' }}>
                  <ProfileActionButton disabled>
                    <Save size={14} />
                    Save
                  </ProfileActionButton>
                </span>
              </DismissibleTooltip>
            ) : (
              <ProfileActionButton onClick={onEditSave} disabled={isDisabled}>
                <Save size={14} />
                {submitting ? 'Saving...' : 'Save'}
              </ProfileActionButton>
            )}
            <ProfileActionButton $variant="primary" onClick={onSaveAsNew} disabled={isDisabled}>
              <Copy size={14} />
              {submitting ? 'Saving...' : 'Save as New'}
            </ProfileActionButton>
          </>
        )}
      </CreateFormFooter>
    </CreateFormContainer>
  )
}
